import { BreakCanvas } from 'classes/break-canvas';
import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachineContext } from 'contexts/machine.context';
import { DOT_SIZE_MD } from 'enums/canvas';
import { PTStep } from 'enums/training.enums';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import {
  PRECISE_PT_MODES,
  PresetTrainingMode,
} from 'lib_ts/enums/training.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { ITrajektRefBreak } from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'react-input-slider';
import useMiscStore from 'stores/use-misc-store';

// how long to wait for all canvas changes before updating img src
const REDRAW_DEQUEUE_MS = 500;

interface IProps {
  actual_x_in: number;
  actual_z_in: number;

  target_x_in: number;
  target_z_in: number;

  shots: IMachineShot[];
  newShotID?: string;

  // not used if disabled
  onUpdate?: (result: ITrajektRefBreak) => void;

  overriding?: boolean;
  disabled?: boolean;

  // true for PT training dialog, false for edit breaks dialog
  training?: boolean;

  // when complete, draw circled checkmark instead of cross on target
  ptStep?: PTStep;

  // if in PRECISE_PT_MODES => filled checkmark on complete, otherwise outlined checkmark
  ptMode?: Nullable<PresetTrainingMode>;
}

export const BreakView = (props: IProps) => {
  const { machine } = useContext(MachineContext);

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  const canvasDef = useMemo(
    () =>
      props.training
        ? BreakCanvas.makeBigSquare(isDarkMode)
        : BreakCanvas.makeSquare(isDarkMode),
    [isDarkMode, props.training]
  );

  // what is used for assembly of the results
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const gridCanvas = useRef<HTMLCanvasElement>(null);
  const rulersCanvas = useRef<HTMLCanvasElement>(null);
  const targetCanvas = useRef<HTMLCanvasElement>(null);
  const shotsCanvas = useRef<HTMLCanvasElement>(null);

  // what gets seen by users + LogRocket
  const [imgSrc, setImgSrc] = useState<string>();
  // for dequeuing updates to the imgSrc
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const updateTimeout = useRef<NodeJS.Timeout>();

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    updateTimeout.current = setTimeout(async () => {
      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [
        gridCanvas.current,
        rulersCanvas.current,
        targetCanvas.current,
        shotsCanvas.current,
      ].filter((c) => c) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  const [slider, setSlider] = useState<ITrajektRefBreak>({
    xInches: props.target_x_in,
    zInches: props.target_z_in,
  });

  // drawRulers
  useEffect(() => {
    try {
      const canvas = rulersCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (props.training) {
        if (machine.isMetric) {
          canvasDef.drawRulersCm(ctx, 75);
        } else {
          canvasDef.drawRulersInches(ctx, 30);
        }
      } else {
        // todo: maybe we want edit breaks to look the same?
        canvasDef.drawRulersForEditBreaks(ctx);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [props.training, machine.isMetric]);

  // drawGrid
  useEffect(() => {
    try {
      const canvas = gridCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!props.training) {
        return;
      }

      canvasDef.drawGrid(ctx, 10);
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [props.training]);

  // drawTarget
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = targetCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!props.training) {
          await canvasDef.drawCrosshairs(ctx, {
            as: 'crosshairs',
            value: slider,
            width_px: 24, // ~magic~ number to make it match w/ plate view's target
            // clear canvas right before drawing to avoid race condition where slider changes while mid-draw
            beforeDraw: () => ctx.clearRect(0, 0, canvas.width, canvas.height),
            color: '#3E63DD',
          });

          await canvasDef.drawTargetBubble(ctx, {
            value: slider,
            width_px: 50, // ~magic~ number to make it match w/ plate view's target
            // clear canvas right before drawing to avoid race condition where slider changes while mid-draw
            beforeDraw: () => ctx.clearRect(0, 0, canvas.width, canvas.height),
          });
          return;
        }

        if (props.ptStep !== PTStep.Complete) {
          await canvasDef.drawCrosshairs(ctx, {
            as: 'cross',
            value: slider,
            width_px: 12, // ~magic~ number to make it match w/ plate view's target
            // no need to clear rect because the bubble above would have done that
            color: `rgb(${StringHelper.hexToRgb('#3E63DD').join(',')})`,
          });
          return;
        }

        const isPrecise =
          props.ptMode && PRECISE_PT_MODES.includes(props.ptMode);

        await canvasDef.drawCheckCircled(ctx, {
          mode: isPrecise ? 'filled' : 'outlined',
          value: slider,
          width_px: 24,
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [slider, props.training, props.ptStep, props.ptMode]);

  // drawBreaks
  useEffect(() => {
    try {
      const canvas = shotsCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (props.overriding) {
        // only draw one green dot for the actual override
        canvasDef.drawDot(ctx, {
          value: {
            xInches: props.actual_x_in,
            zInches: props.actual_z_in,
          },
          radiusIn: 1,
        });
        return;
      }

      // when complete, draw shots w/ transparency behind the checkmark
      ctx.globalAlpha = props.ptStep === PTStep.Complete ? 0.5 : 1;

      // draw a dot for each shot w/ breaks, newest last
      const sortedShots = props.shots.sort((a, b) =>
        StringHelper.safeSort(a._created, b._created)
      );

      sortedShots.forEach((s, i) => {
        const isNew = props.newShotID === s._id;

        const breaks = TrajHelper.getBreaksFromShot(s);

        if (props.training) {
          canvasDef.drawTrainingBreak(ctx, {
            value: {
              xInches: -1 * breaks.xInches,
              zInches: breaks.zInches,
            },
            isNew: isNew,
            size: DOT_SIZE_MD,
            index: i + 1,
          });
          return;
        }

        canvasDef.drawDot(ctx, {
          value: {
            xInches: -1 * breaks.xInches,
            zInches: breaks.zInches,
          },
          radiusIn: 1,
        });
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [
    props.ptStep,
    props.shots,
    props.overriding,
    props.actual_x_in,
    props.actual_z_in,
    slider,
  ]);

  // trigger 1x on mount, then only within onDragEnd to avoid spamming
  useEffect(() => {
    props.onUpdate?.(slider);
  }, []);

  // targetChanged
  useEffect(() => {
    setSlider({
      xInches: props.target_x_in,
      zInches: props.target_z_in,
    });
  }, [props.target_x_in, props.target_z_in]);

  return (
    <ErrorBoundary identifier="BreakView">
      <div className="slider">
        <div
          className="sizer"
          style={{
            aspectRatio: canvasDef.CONFIG.canvas.aspectRatio,
          }}
        >
          {/* for log rocket recordings to show the results */}
          {imgSrc && <img className="image-wrapper" src={imgSrc} />}
          <canvas
            ref={mergeCanvas}
            data-identifier="merge-canvas"
            width={canvasDef.CONFIG.canvas.width_px}
            height={canvasDef.CONFIG.canvas.height_px}
          />
          <canvas
            ref={gridCanvas}
            data-identifier="grid-canvas"
            width={canvasDef.CONFIG.canvas.width_px}
            height={canvasDef.CONFIG.canvas.height_px}
          />
          <canvas
            ref={rulersCanvas}
            data-identifier="rulers-canvas"
            width={canvasDef.CONFIG.canvas.width_px}
            height={canvasDef.CONFIG.canvas.height_px}
          />
          <canvas
            ref={targetCanvas}
            data-identifier="target-canvas"
            width={canvasDef.CONFIG.canvas.width_px}
            height={canvasDef.CONFIG.canvas.height_px}
          />
          <canvas
            ref={shotsCanvas}
            data-identifier="shots-canvas"
            width={canvasDef.CONFIG.canvas.width_px}
            height={canvasDef.CONFIG.canvas.height_px}
          />
          <div
            className={StringHelper.classNames([
              'slider-wrapper',
              props.disabled ? undefined : 'animate-fade',
            ])}
          >
            <Slider
              data-testid="BreakViewSlider"
              data-xcord={slider.xInches}
              data-ycord={slider.zInches}
              axis="xy"
              xstep={canvasDef.CONFIG.x.step}
              xmin={canvasDef.CONFIG.x.min_in}
              xmax={canvasDef.CONFIG.x.max_in}
              x={slider.xInches}
              ystep={canvasDef.CONFIG.y.step}
              ymin={canvasDef.CONFIG.y.min_in}
              ymax={canvasDef.CONFIG.y.max_in}
              y={slider.zInches}
              disabled={props.disabled}
              onChange={(values) =>
                setSlider({
                  xInches: values.x,
                  zInches: values.y,
                })
              }
              onDragEnd={() => {
                props.onUpdate?.(slider);
              }}
              styles={{
                track: {
                  display: 'none',
                  backgroundColor: 'rgba(0, 0, 255, 0)',
                  width: '100%',
                  height: '100%',
                },
                thumb: {
                  display: props.disabled ? 'none' : undefined,
                  backgroundColor: 'rgba(0, 255, 0, 0)',
                  backgroundImage: 'url(/img/crosshair.svg)',
                  backgroundSize: 'cover',
                  boxShadow: 'none',
                  height: 32,
                  width: 32,
                  margin: '-16px',
                },
              }}
              yreverse
            />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
